/**
 * 技術の名前の札。角丸の札の中に、その技術の色つきの正式なロゴを字の高さで置き、右に名前を出す
 * (2026-10-11 オーナーの見本「ロゴもしっかり」)。
 *
 * ロゴは public/assets/tech-logos/ に置いた本物の素材で、自分で描かない。
 * devicon (MIT、https://devicon.dev/) の色つきの版と、devicon に無い Gemini と LangChain は
 * simple-icons (CC0、https://simpleicons.org/) の形に simple-icons が載せている各社の色を付けたもの。
 * Linux の Tux だけは devicon の SVG が 194KB あるので、同じ絵を 64px の webp に焼いた。
 *
 * ロゴの無い名前 (JSP、SQL、DB設計、アルゴリズムなど) は、似た別のロゴで埋めずに字だけの札にする。
 * 埋めると、使っていない技術を使っていることになる (lib/techIcons.tsx と同じ決め)。
 *
 * 札の線、字の大きさと色は、置く節の札のまま (className で渡す)。この部品が持つのは角丸とロゴの並びだけ。
 * 黒い面 (講師の写真の上) ではロゴが沈むので、onDark でロゴの下に白い小さな台を敷く。
 */

import type { ReactNode } from "react";

const LOGO_DIR = "/assets/tech-logos";

/** 名前ごとのロゴのファイル。2つの技術を1枚の札で書いている名前は、両方のロゴを並べる */
const TECH_LOGO_FILES: Record<string, string[]> = {
  Java: ["java.svg"],
  Tomcat: ["tomcat.svg"],
  "JSP / Tomcat": ["tomcat.svg"],
  PostgreSQL: ["postgresql.svg"],
  React: ["react.svg"],
  "Node.js": ["nodejs.svg"],
  "Gemini API": ["gemini.svg"],
  Linux: ["linux.webp"],
  Git: ["git.svg"],
  Python: ["python.svg"],
  AWS: ["aws.svg"],
  TypeScript: ["typescript.svg"],
  "HTML / CSS": ["html5.svg", "css3.svg"],
  HTML: ["html5.svg"],
  CSS: ["css3.svg"],
  JavaScript: ["javascript.svg"],
  LangChain: ["langchain.svg"],
  // OS と LINE。FAQ の窓の固定の答えの名前の印 ({{Mac}} など) が使う。HP の FAQ でロゴを付けるときもここを使う。
  // Apple と Android と Windows は devicon の色つきの版、LINE は HP の LINE のボタンと同じ形 (LINE_ICON_PATH) を LINE の緑で
  Mac: ["apple.svg"],
  iOS: ["apple.svg"],
  iPhone: ["apple.svg"],
  Android: ["android.svg"],
  Windows: ["windows.svg"],
  LINE: ["line.svg"],
};

/**
 * 文の中で名前の前に置くロゴの一覧。OS と端末、会議と連絡の道具、開発の道具の名前。
 * 素材は devicon (MIT) の色つきの版と、simple-icons (CC0) の形に各社の色を付けたもの。色と形は変えない。
 * Mac、iOS、iPhone は Apple のロゴ。
 */
const INLINE_LOGO_FILES = {
  Mac: "apple.svg",
  iOS: "apple.svg",
  iPhone: "apple.svg",
  Windows: "windows.svg",
  Android: "android.svg",
  Zoom: "zoom.svg",
  "Google Meet": "googlemeet.svg",
  Slack: "slack.svg",
  Discord: "discord.svg",
  Mattermost: "mattermost.svg",
  LINE: "line.svg",
  Instagram: "instagram.svg",
  YouTube: "youtube.svg",
  GitHub: "github.svg",
  Figma: "figma.svg",
  Notion: "notion.svg",
  Google: "google.svg",
  Claude: "claude.svg",
  Gemini: "gemini.svg",
  Cloudflare: "cloudflare.svg",
  Docker: "docker.svg",
  Kubernetes: "kubernetes.svg",
  Git: "git.svg",
  AWS: "aws.svg",
  Python: "python.svg",
  JavaScript: "javascript.svg",
  TypeScript: "typescript.svg",
  HTML: "html5.svg",
  CSS: "css3.svg",
  React: "react.svg",
  "Next.js": "nextjs.svg",
  "Node.js": "nodejs.svg",
  Java: "java.svg",
  PHP: "php.svg",
  Ruby: "ruby.svg",
  MySQL: "mysql.svg",
  PostgreSQL: "postgresql.svg",
  Linux: "linux.webp",
} as const;

export type InlineLogoName = keyof typeof INLINE_LOGO_FILES;

/**
 * 文の中で道具や OS の名前の前に置く、字の高さの正式なロゴ。名前の字もこの中に含め、行の途中で折り返しても
 * ロゴと名前が離れないようにする。children を渡すと、名前の代わりにその字を出す (「Zoom勉強会」の Zoom だけ、など)。
 * ロゴの下げ幅は字の大きさ (em) で持つので、どの節の字の大きさでも行の高さを押し上げない。
 */
export function InlineLogo({ name, children }: { name: InlineLogoName; children?: ReactNode }) {
  // Zoom の公式の絵は「Zoom」の字そのもの (横長のロゴ) なので、名前の字は重ねずにロゴだけを置く
  if (name === "Zoom") {
    return (
      <img
        src={`${LOGO_DIR}/${INLINE_LOGO_FILES[name]}`}
        alt="Zoom"
        width={50}
        height={16}
        loading="lazy"
        decoding="async"
        className="mx-[0.1em] inline-block h-[0.95em] w-auto align-[-0.14em]"
      />
    );
  }
  // span ではなく bdi を使う。節の CSS が「札の中の span」に display:block や線を当てることがあるため
  return (
    <bdi className="whitespace-nowrap" style={{ whiteSpace: "nowrap" }}>
      <img
        src={`${LOGO_DIR}/${INLINE_LOGO_FILES[name]}`}
        alt=""
        width={16}
        height={16}
        loading="lazy"
        decoding="async"
        className="mr-[0.2em] inline-block h-[1.05em] w-[1.05em] object-contain align-[-0.16em]"
      />
      {children ?? name}
    </bdi>
  );
}

const LOGO_NAMES_LONGEST_FIRST = (Object.keys(INLINE_LOGO_FILES) as InlineLogoName[]).sort((a, b) => b.length - a.length);
const LOGO_NAME_PATTERN = new RegExp(
  `(?<![A-Za-z0-9.])(${LOGO_NAMES_LONGEST_FIRST.map((n) => n.replace(/\./g, "\\.")).join("|")})(?![A-Za-z0-9])`,
  "g",
);

/**
 * 文字列の中の道具の名前に、最初の 1 回だけロゴを付けた部品の並びを返す。
 * seen を複数の文字列で使い回すと、同じ名前は最初の文字列だけに付く (強調の印で切った段落の中など)。
 */
export function logoNodes(text: string, seen: Set<string> = new Set()): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  for (const m of text.matchAll(LOGO_NAME_PATTERN)) {
    const name = m[1] as InlineLogoName;
    if (seen.has(name)) continue;
    seen.add(name);
    const at = m.index ?? 0;
    if (at > last) out.push(text.slice(last, at));
    out.push(<InlineLogo key={`${name}-${at}`} name={name} />);
    last = at + name.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

/**
 * 文字列の中の道具の名前に、最初の 1 回だけロゴを付けて返す (同じ名前が何度出ても 2 回目からは字のまま)。
 * 台本や一覧のデータが文字列で渡ってくる所で使う。skip に名前を渡すと、その名前には付けない。
 */
export function LogoText({ text, skip = [] }: { text: string; skip?: InlineLogoName[] }) {
  return <>{logoNodes(text, new Set<string>(skip))}</>;
}

const SERVICE_LOGOS = {
  ゆめさくコード: { src: "/assets/brand/yumesaku-code-favicon.svg", w: 1, h: 1 },
  // 研究所の正式なロゴ (apps/client/public/yumesaku_lab_logo.webp) の、フラスコの部分だけを切り出したもの
  ゆめさく研究所: { src: "/assets/brand/yumesaku-lab-flask.webp", w: 56, h: 66 },
} as const;

/**
 * 自社のサービスの名前の頭に置く、正式なロゴ (ゆめさくコードは favicon、研究所はフラスコ)。
 * 名前の字もこの中に含めるので、折り返しても離れない。黒い面では onDark で白い角丸の台に載せる。
 */
export function ServiceName({
  name,
  onDark = false,
  children,
}: {
  name: keyof typeof SERVICE_LOGOS;
  onDark?: boolean;
  children?: ReactNode;
}) {
  const logo = SERVICE_LOGOS[name];
  const flask = name === "ゆめさく研究所";
  return (
    <span className="whitespace-nowrap">
      <img
        src={logo.src}
        alt=""
        width={flask ? 17 : 18}
        height={18}
        loading="lazy"
        decoding="async"
        className={`mr-[0.3em] inline-block h-[1.1em] shrink-0 align-[-0.2em] object-contain ${
          flask ? "w-[0.95em]" : "w-[1.1em] rounded-[3px]"
        } ${onDark && flask ? "box-content rounded-[4px] bg-white p-[0.12em]" : ""}`}
      />
      {children ?? name}
    </span>
  );
}

/** ロゴの住所 (公開の置き場からの絶対パス)。basename は付けないので、/women の下で使うときは sitePath を通す */
export function techLogoPaths(name: string): string[] {
  return techLogoFiles(name).map((file) => `${LOGO_DIR}/${file}`);
}

export function techLogoFiles(name: string): string[] {
  return Object.hasOwn(TECH_LOGO_FILES, name) ? TECH_LOGO_FILES[name] : [];
}

export function TechChip({
  name,
  className = "",
  onDark = false,
}: {
  name: string;
  className?: string;
  onDark?: boolean;
}) {
  const files = techLogoFiles(name);
  return (
    <span className={`inline-flex items-center gap-[0.4em] ${className}`} style={{ borderRadius: 999 }}>
      {/* 中に span を入れない。節の CSS が「札の中の span」に線と余白を当てているため */}
      {files.map((file) => (
        <img
          key={file}
          src={`${LOGO_DIR}/${file}`}
          alt=""
          width={16}
          height={16}
          loading="lazy"
          decoding="async"
          className={
            onDark
              ? "block h-[1.35em] w-[1.35em] shrink-0 rounded-[3px] bg-white object-contain p-[1px]"
              : "block h-[1.25em] w-[1.25em] shrink-0 object-contain"
          }
        />
      ))}
      {name}
    </span>
  );
}
